import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, Col, Divider, Empty, Form, Input, message, Pagination, Row, Select, Switch, Typography } from 'antd';
import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { getDeviceProperty } from '@/api';
import { Tab1Modal, Tab1ModalRef } from './Tab1Modal';
import { deviceControl, DeviceProperty, getDevicePropertyCodeClassifications } from '@/api/device-thingmodel';
import { isNull, isObject } from 'lodash';
import { useUpdateEffect } from 'ahooks';
import { WriteValueModal } from './WriteValueModal';

const { Text, Title } = Typography;

export interface DetailTab1Props {
  product_id?: string;
}

export const DetailTab1: React.FC<DetailTab1Props> = ({ product_id }) => {
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const timer = useRef<ReturnType<typeof setInterval>>();
  const tab1ModalRef = useRef<Tab1ModalRef>(null);

  // 卡片列表数据
  const [lists, setLists] = useState<DeviceProperty[]>([]);
  // 是否开启实时刷新
  const [checked, setChecked] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  // 按属性 code 记录开关/写入的 loading
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});

  // 列表请求参数（分页 + 查询 + 标识符分类）
  const [pageParams, setPageParams] = useState<{ page: number; pageSize: number; info?: string; tag?: string }>({ page: 1, pageSize: 10 });

  // 标识符分类下拉选项
  const [codeClassifications, setCodeClassifications] = useState<string[]>([]);

  const [writeModalVisible, setWriteModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState<DeviceProperty>();

  const load = useCallback(
    (changeParams?: { page?: number; pageSize?: number; info?: string; code?: string; tag?: string }) => {
      // 拉取属性列表：支持分页 + 模糊查询 + 标识符分类
      getDeviceProperty({ id: params.id!, ...pageParams, ...changeParams }).then((resp) => {
        console.log(resp, { changeParams });
        if (resp.success) {
          setLists(resp.result.list);
          setTotal(resp.result.total);

          const code = searchParams.get('code');

          if (code) {
            const item = resp.result.list.find((item) => item.code === code);

            item && setTimeout(() => tab1ModalRef.current?.open(item), 0);
          }
        }
      });
    },
    [params, pageParams],
  );

  useUpdateEffect(() => {
    if (checked) {
      // 开启实时刷新：每 3 秒刷新一次
      timer.current = setInterval(() => {
        load();
      }, 3000);
    } else {
      clearInterval(timer.current);
    }

    return () => {
      clearInterval(timer.current);
    };
  }, [checked]);

  useEffect(() => {
    // 首次进入：如果 url 带了 code，则按 code 作为查询条件
    load(searchParams.get('code') ? { info: searchParams.get('code') as string } : {});
  }, []);

  useEffect(() => {
    // 拉取设备标识符分类
    if (!params.id) return;
    getDevicePropertyCodeClassifications({ id: params.id })
      .then((resp) => {
        if (resp.success) {
          setCodeClassifications(resp.result || []);
        } else {
          message.error(resp.errorMsg);
        }
      })
      .catch(() => {
        // httpRequest 不会自动 toast，这里兜底提示
        message.error('获取标识符分类失败');
      });
  }, [params.id]);

  // if (!lists.length) {
  //   return <Empty />;
  // }

  const handleChange = async (item: DeviceProperty) => {
    // bool 类型开关控制
    setLoading((prev) => ({ ...prev, [item.code]: true }));
    try {
      const res = await deviceControl({ deviceId: params.id!, data: { [item.code]: !item.value } });
      if (res.success) {
        message.success('操作成功');
        load();
      } else {
        message.error(res.errorMsg);
      }
      setLoading((prev) => ({ ...prev, [item.code]: false }));
    } catch (error) {
      setLoading((prev) => ({ ...prev, [item.code]: false }));
      console.log(error);
    }
  };

  const handleWriteValue = async (value: string) => {
    if (!currentItem) return;

    // 写值：根据数据类型做一次转换
    let convertedValue: string | number = value;

    if (currentItem.data_type === 'int' || currentItem.data_type === 'float') {
      convertedValue = Number(value);
    }

    try {
      const res = await deviceControl({ deviceId: params.id!, data: { [currentItem.code]: convertedValue } } as any);
      if (res.success) {
        message.success('写入成功');
        load();
        setWriteModalVisible(false);
      } else {
        message.error(res.errorMsg);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Tab1Modal ref={tab1ModalRef} product_id={product_id}></Tab1Modal>
      <WriteValueModal open={writeModalVisible} onCancel={() => setWriteModalVisible(false)} onOk={handleWriteValue} item={currentItem} />
      <div className="mb10" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Form layout="inline" initialValues={{ code: searchParams.get('code'), tag: pageParams.tag }}>
          <Form.Item name="tag">
            <Select
              placeholder="请选择分类"
              style={{ width: 180 }}
              allowClear
              showSearch={false}
              options={codeClassifications.map((v) => ({ label: v, value: v }))}
              onChange={(tag) => {
                setPageParams({ ...pageParams, page: 1, tag });
                load({ page: 1, tag });
              }}
            />
          </Form.Item>
          <Form.Item name="code">
            <Input.Search
              placeholder="请输入查询条件"
              allowClear
              onSearch={(info) => {
                setPageParams({ ...pageParams, page: 1, info });
                load({ page: 1, info });
              }}
            ></Input.Search>
          </Form.Item>
        </Form>
        <Form layout="inline">
          <Form.Item label="实时刷新">
            <Switch checked={checked} onChange={setChecked} />
          </Form.Item>
        </Form>
      </div>
      {lists?.length === 0 && <Empty />}
      <Row gutter={[6, 6]}>
        {lists.map((item, index) => {
          return (
            <Col span={6} key={index}>
              <Card hoverable size="small">
                <div className="justify-space-between">
                  <div>
                    <Text style={{ color: '#87909d', fontSize: 13 }}>{item.name}</Text>
                  </div>
                  <div>
                    <Text
                      className="on-hover"
                      style={{ fontSize: 12 }}
                      onClick={() => {
                        tab1ModalRef.current?.open(item);
                      }}
                    >
                      <HistoryOutlined className="mr5" />
                      历史数据
                    </Text>
                  </div>
                </div>
                <div className="mt10 mb20 align-center justify-space-between">
                  <Title level={5} ellipsis={{ tooltip: isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value) }}>
                    {isNull(item.value) ? '-' : isObject(item.value) ? JSON.stringify(item.value) : String(item.value)}
                    {item.unit && <> ({item.unit})</>}
                  </Title>
                  <div className="align-center">
                    {item?.data_type !== 'bool' && item?.access_mode === 'RW' && (
                      <EditOutlined
                        style={{ fontSize: '14px', cursor: 'pointer' }}
                        onClick={() => {
                          setCurrentItem(item);
                          setWriteModalVisible(true);
                        }}
                      />
                    )}
                    {item?.data_type === 'bool' && item?.access_mode === 'RW' && (
                      <Switch key={item.code} checked={!!lists.find((v) => v.code === item.code)?.value} onChange={() => handleChange(item)} loading={loading[item.code]} className="ml10" />
                    )}
                  </div>
                </div>
                <div className="mt20">
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(item.time).format('YYYY-MM-DD HH:mm:ss')}
                    <Divider type="vertical" />
                    {item.data_type}
                    <Divider type="vertical" />
                    {item.access_mode === 'R' && '只读'}
                    {item.access_mode === 'RW' && '读写'}
                  </Text>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
      <div className="mt20" style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Pagination
          current={pageParams.page}
          pageSize={pageParams.pageSize}
          onChange={(page, pageSize) => {
            setPageParams({ ...pageParams, page, pageSize });
            load({ page, pageSize });
          }}
          showSizeChanger
          showTotal={(total) => `共 ${total} 条`}
          showQuickJumper
          showPrevNextJumpers
          total={total}
        ></Pagination>
      </div>
    </>
  );
};

export default DetailTab1;
